import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fmtDate } from "@/lib/helpers";
import { Printer, Clock, Gavel, ListChecks, CalendarClock, AlertCircle } from "lucide-react";

function HearingRow({ h, onClick }) {
  return (
    <button onClick={onClick} className="flex w-full items-start gap-4 px-5 py-3.5 text-left transition-colors hover:bg-secondary/50">
      <div className="flex w-16 shrink-0 flex-col items-center rounded-md bg-navy/5 py-1.5">
        <Clock className="h-3.5 w-3.5 text-navy" />
        <span className="mt-0.5 text-xs font-semibold text-navy">{h.time || "—"}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-navy">{h.case_title}</p>
        <p className="truncate text-xs text-muted-foreground">{h.case_number} · {h.court} · {h.purpose}</p>
        {h.client_name && <p className="truncate text-xs text-muted-foreground">Client: {h.client_name}</p>}
      </div>
      <StatusBadge status={h.status} />
    </button>
  );
}

function DiarySection({ title, icon: Icon, items, empty, render }) {
  return (
    <Card className="border-border shadow-sm">
      <div className="flex items-center gap-2 border-b border-border px-5 py-4">
        <Icon className="h-4 w-4 text-gold" />
        <h2 className="font-display text-xl text-navy">{title}</h2>
        <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{items.length}</span>
      </div>
      <div className="divide-y divide-border">
        {items.length === 0 ? <div className="px-5 py-8 text-center text-sm text-muted-foreground">{empty}</div> : items.map(render)}
      </div>
    </Card>
  );
}

export default function MyDiary() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/diary").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <LoadingState />;

  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="diary-page" className="print:bg-white">
      <PageHeader
        title="My Diary"
        subtitle={today}
        actions={<Button variant="outline" onClick={() => window.print()} data-testid="print-diary-btn" className="print:hidden"><Printer className="mr-1.5 h-4 w-4" /> Print / Export</Button>}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DiarySection title="Today's Hearings" icon={Gavel} items={data.today} empty="No hearings today."
          render={(h) => <HearingRow key={h.id} h={h} onClick={() => navigate(`/cases/${h.case_id}`)} />} />
        <DiarySection title="Tomorrow's Hearings" icon={CalendarClock} items={data.tomorrow} empty="No hearings tomorrow."
          render={(h) => <HearingRow key={h.id} h={h} onClick={() => navigate(`/cases/${h.case_id}`)} />} />
        <DiarySection title="Upcoming Hearings" icon={CalendarClock} items={data.upcoming} empty="Nothing in the next 7 days."
          render={(h) => (
            <button key={h.id} onClick={() => navigate(`/cases/${h.case_id}`)} className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-secondary/50">
              <span className="w-24 shrink-0 text-xs font-medium text-navy">{fmtDate(h.date)}</span>
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-navy">{h.case_title}</p><p className="truncate text-xs text-muted-foreground">{h.court} · {h.time}</p></div>
            </button>
          )} />
        <DiarySection title="Tasks & Deadlines" icon={ListChecks} items={data.deadlines} empty="No upcoming deadlines."
          render={(t) => (
            <div key={t.id} className="flex items-center justify-between px-5 py-3">
              <div className="flex items-center gap-2">
                {t.due_date < data.date && <AlertCircle className="h-4 w-4 text-rose-500" />}
                <div><p className="text-sm font-medium text-navy">{t.title}</p><p className="text-xs text-muted-foreground">Due {fmtDate(t.due_date)}</p></div>
              </div>
              <StatusBadge status={t.status} />
            </div>
          )} />
      </div>
    </div>
  );
}
